/**
 * NewConversationPanel — search for users and start a conversation.
 *
 * Flow:
 *   1. User types a name/screen name/email in the search box
 *   2. Typeahead shows matching platform users
 *   3. Select one user → start a DM (sends contact request if needed)
 *   4. Select multiple → create a group chat
 *
 * For MVP: Uses the already-cached SecurityUsers data.
 * Future: Could add a dedicated search API with pagination.
 */
import React, { useState, useCallback } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import type { SecurityUserResponse } from '../../api/security';
import type { ConversationResponse } from '../../api/chat';
import { createConversation, sendContactRequest, fetchContacts, fetchConversation } from '../../api/chat';
import { mediaSrc } from '../../api/mediaUrl';
import UserSearchInput from './UserSearchInput';
import styles from './Chat.module.css';

interface NewConversationPanelProps {
  /** All platform users */
  users: SecurityUserResponse[];
  currentUserGuid: string;
  /** Called when a conversation is created or found */
  onConversationCreated: (conv: ConversationResponse) => void;
  onCancel: () => void;
}

export default function NewConversationPanel({
  users,
  currentUserGuid,
  onConversationCreated,
  onCancel,
}: NewConversationPanelProps) {
  const { t } = useAppTranslation('chat');
  const [selectedUsers, setSelectedUsers] = useState<SecurityUserResponse[]>([]);
  const [groupName, setGroupName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const handleSelectUser = useCallback((user: SecurityUserResponse) => {
    setSelectedUsers((prev) => {
      if (prev.some((u) => u.userGuid.toUpperCase() === user.userGuid.toUpperCase())) {
        return prev; // Already selected
      }
      return [...prev, user];
    });
    setError(null);
  }, []);

  const handleRemoveUser = useCallback((userGuid: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.userGuid.toUpperCase() !== userGuid.toUpperCase()));
  }, []);

  const handleCreate = useCallback(async () => {
    if (selectedUsers.length === 0) return;

    setCreating(true);
    setError(null);
    setInfo(null);

    try {
      const participantGuids = selectedUsers.map((u) => u.userGuid);

      if (selectedUsers.length === 1) {
        // DM flow — check if already accepted contacts
        const otherGuid = participantGuids[0];
        let isAcceptedContact = false;

        try {
          const contacts = await fetchContacts();
          isAcceptedContact = contacts.some(
            (c) =>
              (c.requesterGuid.toUpperCase() === otherGuid.toUpperCase() ||
               c.recipientGuid.toUpperCase() === otherGuid.toUpperCase())
          );
        } catch {
          // If we can't fetch contacts, try anyway
        }

        if (isAcceptedContact) {
          // Already contacts — create conversation directly
          try {
            const conv = await createConversation({
              conversationType: 'direct',
              participantGuids,
            });
            onConversationCreated(conv);
          } catch (createErr: any) {
            // If conversation already exists, navigate to the existing one
            if (createErr.body?.conversationId) {
              const existing = await fetchConversation(createErr.body.conversationId);
              onConversationCreated(existing);
            } else {
              throw createErr; // Re-throw for outer catch
            }
          }
        } else {
          // Not yet contacts — send request and inform the user
          try {
            const result = await sendContactRequest(otherGuid);
            if (result.status === 'accepted') {
              // Mutual request — auto-accepted! Create conversation now.
              const conv = await createConversation({
                conversationType: 'direct',
                participantGuids,
              });
              onConversationCreated(conv);
            } else {
              // Request is pending — tell the user
              setInfo(t('contacts.requestSent') + '. ' + t('create.waitForAccept'));
              setCreating(false);
            }
          } catch (err: any) {
            if (err.message?.includes('Already contacts')) {
              // Race condition — they became contacts between the check and now
              const conv = await createConversation({
                conversationType: 'direct',
                participantGuids,
              });
              onConversationCreated(conv);
            } else if (err.message?.includes('already pending')) {
              // Request was already sent — show info, not error
              setInfo(t('contacts.requestSent') + '. ' + t('create.waitForAccept'));
              setCreating(false);
            } else {
              setError(err.message || 'Failed to send contact request');
            }
          }
        }
      } else {
        // Group chat — create directly (no contact requirement)
        const conv = await createConversation({
          conversationType: 'group',
          name: groupName.trim() || undefined,
          participantGuids,
        });
        onConversationCreated(conv);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create conversation');
    } finally {
      setCreating(false);
    }
  }, [selectedUsers, groupName, onConversationCreated, t]);

  const excludeGuids = [
    currentUserGuid,
    ...selectedUsers.map((u) => u.userGuid),
  ];

  const isGroup = selectedUsers.length > 1;

  return (
    <div className={styles.newConvPanel}>
      <div className={styles.newConvHeader}>
        <h3 className={styles.newConvTitle}>{t('create.title')}</h3>
      </div>

      {/* User search */}
      <div className={styles.newConvSearch}>
        <UserSearchInput
          users={users}
          excludeGuids={excludeGuids}
          onSelect={handleSelectUser}
        />
      </div>

      {/* Selected users chips */}
      {selectedUsers.length > 0 && (
        <div className={styles.selectedChips}>
          {selectedUsers.map((user) => (
            <span key={user.userGuid} className={styles.userChip}>
              {user.profileImageUrl ? (
                <img
                  src={mediaSrc(user.profileImageUrl) ?? undefined}
                  alt=""
                  className={styles.chipAvatar}
                />
              ) : (
                <span className={styles.chipAvatarInitial}>
                  {(user.displayName || user.name || '?')[0].toUpperCase()}
                </span>
              )}
              <span className={styles.chipName}>
                {user.displayName || user.name}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => handleRemoveUser(user.userGuid)}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Group name input (only for 2+ users) */}
      {isGroup && (
        <div className={styles.newConvGroupName}>
          <input
            type="text"
            className={styles.groupNameInput}
            placeholder={t('create.groupNamePlaceholder')}
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={styles.newConvError}>{error}</div>
      )}

      {/* Info (e.g. contact request pending) */}
      {info && (
        <div className={styles.newConvInfo}>{info}</div>
      )}

      {/* Actions */}
      <div className={styles.newConvActions}>
        <button
          type="button"
          className={styles.newConvCancelBtn}
          onClick={onCancel}
        >
          {t('create.cancel')}
        </button>
        <button
          type="button"
          className={styles.newConvCreateBtn}
          onClick={handleCreate}
          disabled={selectedUsers.length === 0 || creating}
        >
          {creating ? '…' : t('create.create')}
        </button>
      </div>
    </div>
  );
}
